import React, { createContext, useState, useCallback, useMemo } from 'react';
import { setTokens, clearTokens, hasAccessToken } from 'data/connection/storage';
import { subscriptionClient, client } from 'data/connection/client.connection';

type AuthState = {
    authenticated: boolean;
    loading: boolean;
};

type AuthActions = {
    login: (token: string, refreshToken: string) => Promise<void>;
    logout: () => void;
    setPassword: (token: string, refreshToken: string) => Promise<void>;
    signup: (token: string, refreshToken: string) => Promise<void>;
};

export type AuthContextType = {
    auth: AuthState;
    authActions: AuthActions;
};

export const AuthContext = createContext<AuthContextType | null>(null);

type AuthProviderProps = {
    onAuthChange?: () => void;
    children: React.ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ onAuthChange, children }) => {
    const [auth, setAuth] = useState<AuthState>({
        authenticated: hasAccessToken(),
        loading: false,
    });

    const handleTokens = useCallback(
        async (token: string, refreshToken: string) => {
            await client.clearStore().catch(() => {});
            setTokens(token, refreshToken);
            subscriptionClient.terminate();
            setAuth({ authenticated: true, loading: false });
            onAuthChange?.();
        },
        [onAuthChange],
    );

    const login = useCallback(
        async (token: string, refreshToken: string) => {
            await handleTokens(token, refreshToken);
        },
        [handleTokens],
    );

    const signup = useCallback(
        async (token: string, refreshToken: string) => {
            await handleTokens(token, refreshToken);
        },
        [handleTokens],
    );

    const setPassword = useCallback(
        async (token: string, refreshToken: string) => {
            await handleTokens(token, refreshToken);
        },
        [handleTokens],
    );

    const logout = useCallback(() => {
        clearTokens();
        setAuth({ authenticated: false, loading: false });
        subscriptionClient.terminate();
        client.clearStore().catch(() => {});
        onAuthChange?.();
    }, [onAuthChange]);

    const value = useMemo(
        () => ({
            auth,
            authActions: { login, logout, signup, setPassword },
        }),
        [auth, login, logout, signup, setPassword],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
